class VehicleFilterAPI {
  constructor() {
    this.form = document.querySelector('.vehicle-filter-form');
    if (!this.form) return;

    this.apiUrl = this.form.dataset.apiUrl;
    this.makeSelect = document.getElementById('vehicle-make');
    this.modelSelect = document.getElementById('vehicle-model');
    this.yearSelect = document.getElementById('vehicle-year');
    this.submitBtn = document.getElementById('vehicle-filter-submit');
    this.loadingEl = this.form.querySelector('.vehicle-filter-form__loading');

    this.selectedMake = null;
    this.selectedModel = null;
    this.selectedYear = null;

    this.isFiltering = false;
    
    // CSV data structure
    this.csvUrl = 'https://cdn.shopify.com/s/files/1/0778/4038/4259/files/makemodelyear.csv?v=1767716396';
    this.vehicleData = {
      makes: new Set(),
      modelsByMake: {}, // { make: Set of models }
      yearsByMakeModel: {} // { make: { model: Set of years } }
    };
    
    // Collection product data structure (for filtering by collection)
    this.collectionData = {
      makes: new Set(),
      modelsByMake: {}, // { make: Set of models }
      yearsByMakeModel: {} // { make: { model: Set of years } }
    };
    this.isCollectionPage = false;
    this.collectionHandle = null;

    this.init();
  }

  async init() {
    await this.loadCSVData();
    await this.loadCollectionData();
    // Only load makes after collection data is loaded (if on collection page)
    // This ensures dropdowns are filtered by collection products
    await this.loadMakes();
    this.attachEventListeners();
  }
  
  async loadCollectionData() {
    // Check if we're on a collection page
    const pathMatch = window.location.pathname.match(/\/collections\/([^\/\?]+)/);
    if (!pathMatch) {
      // Not on a collection page, skip collection filtering
      return;
    }
    
    this.isCollectionPage = true;
    this.collectionHandle = pathMatch[1];
    
    // Skip 'all' collection - treat it as not a specific collection
    if (this.collectionHandle === 'all') {
      this.isCollectionPage = false;
      return;
    }
    
    this.showLoading(true);
    try {
      
      // Fetch all products from collection (handle pagination)
      // Template paginates by 50 (Shopify's limit), so we may need multiple requests
      let allProducts = [];
      let page = 1;
      let hasMore = true;
      const maxPages = 20; // Safety limit (50 products per page * 20 = 1000 products max)
      
      while (hasMore && page <= maxPages) {
        const url = page === 1 
          ? `/collections/${this.collectionHandle}?view=vehicle-data`
          : `/collections/${this.collectionHandle}?view=vehicle-data&page=${page}`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          console.warn(`Failed to fetch collection products page ${page}: ${response.status}`);
          break;
        }
        
        // Try to parse as JSON first (regardless of content-type)
        // Shopify templates might not always set the correct content-type header
        let products;
        try {
          const text = await response.text();
          
          // Try to parse as JSON
          try {
            products = JSON.parse(text);
            
            // Check if it's an error object
            if (products && products.error) {
              console.error('Template returned error:', products.error);
              break;
            }
          } catch (parseError) {
            // Not valid JSON - check content-type for debugging
            const contentType = response.headers.get('content-type');
            console.warn(`Failed to parse JSON for page ${page}`);
            console.warn(`Content-Type: ${contentType}`);
            console.warn('Response text (first 500 chars):', text.substring(0, 500));
            break;
          }
        } catch (error) {
          console.error(`Failed to read response for page ${page}:`, error);
          break;
        }
        
        if (!products || !Array.isArray(products) || products.length === 0) {
          hasMore = false;
        } else {
          allProducts = allProducts.concat(products);
          // Template paginates by 50, so if we get less, we're done
          if (products.length < 50) {
            hasMore = false;
          } else {
            page++;
          }
        }
      }
      
      if (allProducts.length === 0) {
        console.warn('⚠️ No products loaded from collection. Check if template is returning data correctly.');
        return;
      }
      
      // Extract vehicle data from collection products
      let processedCount = 0;
      allProducts.forEach((product, index) => {
        const make = this.extractValue(product.make);
        const model = this.extractValue(product.model);
        const year = this.extractValue(product.year);
        
        
        // Handle arrays (list-type metafields) - process all combinations
        const makes = Array.isArray(make) ? make : (make ? [make] : []);
        const models = Array.isArray(model) ? model : (model ? [model] : []);
        const years = Array.isArray(year) ? year : (year ? [year] : []);
        
        // If no make, skip this product
        if (makes.length === 0) {
          if (index < 3) {
            console.warn(`⚠️ Product ${product.id} has no make value`);
          }
          return;
        }
        
        processedCount++;
        
        // Process all combinations of make/model/year
        makes.forEach(m => {
          const makeStr = String(m).trim();
          if (!makeStr) return;
          
          this.collectionData.makes.add(makeStr);
          
          if (models.length > 0) {
            models.forEach(mod => {
              const modelStr = String(mod).trim();
              if (!modelStr) return;
              
              if (!this.collectionData.modelsByMake[makeStr]) {
                this.collectionData.modelsByMake[makeStr] = new Set();
              }
              this.collectionData.modelsByMake[makeStr].add(modelStr);
              
              if (years.length > 0) {
                years.forEach(y => {
                  const yearStr = String(y).trim();
                  if (!yearStr) return;
                  
                  if (!this.collectionData.yearsByMakeModel[makeStr]) {
                    this.collectionData.yearsByMakeModel[makeStr] = {};
                  }
                  if (!this.collectionData.yearsByMakeModel[makeStr][modelStr]) {
                    this.collectionData.yearsByMakeModel[makeStr][modelStr] = new Set();
                  }
                  this.collectionData.yearsByMakeModel[makeStr][modelStr].add(yearStr);
                });
              }
            });
          }
        });
      });
      
    } catch (error) {
      console.error('Error loading collection data:', error);
    } finally {
      this.showLoading(false);
    }
  }
  
  extractValue(value) {
    // Handle both array and string values
    // For arrays, we need to process all values (not just first)
    // But for building the filter structure, we'll use all values
    if (!value) return null;
    
    if (Array.isArray(value)) {
      // Return array of non-null values
      return value.filter(v => v != null && v !== '');
    }
    
    return value;
  }
  
  async loadCSVData() {
    this.showLoading(true);
    try {
      const response = await fetch(this.csvUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch CSV: ${response.status}`);
      }
      
      const csvText = await response.text();
      this.parseCSV(csvText);
    } catch (error) {
      console.error('Error loading CSV data:', error);
      this.showError(this.makeSelect, 'Error loading vehicle data');
    } finally {
      this.showLoading(false);
    }
  }
  
  parseCSV(csvText) {
    const lines = csvText.trim().split('\n');
    
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parse CSV line (handling quoted values)
      const values = this.parseCSVLine(line);
      if (values.length < 4) continue;
      
      const [, make, model, year] = values;
      
      if (!make || !model || !year) continue;
      
      // Normalize values (trim whitespace)
      const normalizedMake = make.trim();
      const normalizedModel = model.trim();
      const normalizedYear = year.trim();
      
      // Add to data structure
      this.vehicleData.makes.add(normalizedMake);
      
      // Initialize modelsByMake if needed
      if (!this.vehicleData.modelsByMake[normalizedMake]) {
        this.vehicleData.modelsByMake[normalizedMake] = new Set();
      }
      this.vehicleData.modelsByMake[normalizedMake].add(normalizedModel);
      
      // Initialize yearsByMakeModel if needed
      if (!this.vehicleData.yearsByMakeModel[normalizedMake]) {
        this.vehicleData.yearsByMakeModel[normalizedMake] = {};
      }
      if (!this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel]) {
        this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel] = new Set();
      }
      this.vehicleData.yearsByMakeModel[normalizedMake][normalizedModel].add(normalizedYear);
    }
  }
  
  parseCSVLine(line) {
    const values = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current); // Push last value
    
    return values;
  }

  attachEventListeners() {
    if (!this.makeSelect || !this.modelSelect || !this.yearSelect || !this.submitBtn) {
      console.error('Required form elements not found');
      return;
    }

    this.makeSelect.addEventListener('change', async (e) => {
      const target = e.target;
      if (!target || !(target instanceof HTMLSelectElement)) return;
      
      this.selectedMake = target.value;
      this.selectedModel = null;
      this.selectedYear = null;

      if (this.selectedMake) {
        await this.loadModels();
        if (this.submitBtn) this.submitBtn.disabled = false;
      } else {
        this.resetModelSelect();
        this.resetYearSelect();
        if (this.submitBtn) this.submitBtn.disabled = true;
      }
    });

    this.modelSelect.addEventListener('change', async (e) => {
      const target = e.target;
      if (!target || !(target instanceof HTMLSelectElement)) return;
      
      this.selectedModel = target.value;
      this.selectedYear = null;

      if (this.selectedModel) {
        await this.loadYears();
      } else {
        this.resetYearSelect();
      }
    });

    this.yearSelect.addEventListener('change', (e) => {
      const target = e.target;
      if (!target || !(target instanceof HTMLSelectElement)) return;
      
      this.selectedYear = target.value;
    });

    this.submitBtn.addEventListener('click', () => {
      this.handleSubmit();
    });
  }

  async loadMakes() {
    if (!this.makeSelect) return;
    
    this.showLoading(true);
    try {
      // Fetch makes from API
      const data = await this.fetchFilters();
      
      if (data.success && data.data.makes && data.data.makes.length > 0) {
        // Filter makes: must exist in CSV AND (collection if on collection page)
        let validMakes = data.data.makes.filter(make => 
          this.vehicleData.makes.has(make.trim())
        );
        
        
        // If on collection page, ONLY show makes that exist in collection products
        if (this.isCollectionPage) {
          if (this.collectionData.makes.size === 0) {
            // Collection has no products with vehicle data, show no makes
            console.warn('⚠️ Collection has no products with vehicle data');
            validMakes = [];
          } else {
            const beforeFilter = validMakes.length;
            validMakes = validMakes.filter(make => 
              this.collectionData.makes.has(make.trim())
            );
            
          }
        }
        
        if (validMakes.length > 0) {
          this.populateSelect(this.makeSelect, validMakes.sort(), 'Select Make');
          this.makeSelect.disabled = false;
        } else {
          console.error('❌ No valid makes available. Reasons:', {
            apiHasMakes: data.data.makes.length > 0,
            csvHasMakes: this.vehicleData.makes.size > 0,
            isCollectionPage: this.isCollectionPage,
            collectionHasMakes: this.collectionData.makes.size > 0,
            apiMakesSample: data.data.makes.slice(0, 3),
            csvMakesSample: Array.from(this.vehicleData.makes).slice(0, 3),
            collectionMakesSample: Array.from(this.collectionData.makes).slice(0, 3)
          });
          this.showError(this.makeSelect, 'No valid makes available');
        }
      } else {
        this.showError(this.makeSelect, 'Error loading makes');
      }
    } catch (error) {
      console.error('Error loading makes:', error);
      this.showError(this.makeSelect, 'Error loading makes');
    } finally {
      this.showLoading(false);
    }
  }

  async loadModels() {
    if (!this.modelSelect) return;
    
    this.showLoading(true);
    try {
      if (!this.selectedMake) {
        this.resetModelSelect();
        return;
      }
      
      // Fetch models from API
      const data = await this.fetchFilters({ make: this.selectedMake });
      
      if (data.success && data.data.models && data.data.models.length > 0) {
      // Filter models: must exist in CSV for the selected make
      const csvModelsSet = this.vehicleData.modelsByMake[this.selectedMake];
      let validModels = data.data.models.filter(model => 
        csvModelsSet && csvModelsSet.has(model.trim())
      );
      
      // If on collection page, ONLY show models that exist in collection products
      if (this.isCollectionPage) {
        const collectionModelsSet = this.collectionData.modelsByMake[this.selectedMake];
        if (!collectionModelsSet || collectionModelsSet.size === 0) {
          // Collection has no models for this make, show no models
          validModels = [];
        } else {
          validModels = validModels.filter(model => 
            collectionModelsSet.has(model.trim())
          );
        }
      }
        
        if (validModels.length > 0) {
          this.populateSelect(this.modelSelect, validModels.sort(), 'Select Model');
          this.modelSelect.disabled = false;
        } else {
          this.showError(this.modelSelect, 'No valid models available for this make');
        }
      } else {
        this.showError(this.modelSelect, 'Error loading models');
      }
      
      // Reset year select when model changes
      this.resetYearSelect();
    } catch (error) {
      console.error('Error loading models:', error);
      this.showError(this.modelSelect, 'Error loading models');
    } finally {
      this.showLoading(false);
    }
  }

  async loadYears() {
    if (!this.yearSelect) return;
    
    this.showLoading(true);
    try {
      if (!this.selectedMake || !this.selectedModel) {
        this.resetYearSelect();
        return;
      }
      
      // Fetch years from API
      const data = await this.fetchFilters({ 
        make: this.selectedMake, 
        model: this.selectedModel 
      });
      
      if (data.success && data.data.years && data.data.years.length > 0) {
      // Filter years: must exist in CSV for the selected make+model
      const csvYearsSet = this.vehicleData.yearsByMakeModel[this.selectedMake]?.[this.selectedModel];
      let validYears = data.data.years.filter(year => 
        csvYearsSet && csvYearsSet.has(year.trim())
      );
      
      // If on collection page, ONLY show years that exist in collection products
      if (this.isCollectionPage) {
        const collectionYearsSet = this.collectionData.yearsByMakeModel[this.selectedMake]?.[this.selectedModel];
        if (!collectionYearsSet || collectionYearsSet.size === 0) {
          // Collection has no years for this make+model, show no years
          validYears = [];
        } else {
          validYears = validYears.filter(year => 
            collectionYearsSet.has(year.trim())
          );
        }
      }
        
        if (validYears.length > 0) {
          // Sort years: numeric years descending, then non-numeric
          const numericYears = validYears
            .map(year => parseInt(year.trim(), 10))
            .filter(year => !isNaN(year))
            .sort((a, b) => b - a)
            .map(year => year.toString());
          
          const nonNumericYears = validYears
            .filter(year => isNaN(parseInt(year.trim(), 10)))
            .sort();
          
          const allYears = [...numericYears, ...nonNumericYears];
          
          this.populateSelect(this.yearSelect, allYears, 'Select Year');
          this.yearSelect.disabled = false;
        } else {
          this.showError(this.yearSelect, 'No valid years available for this model');
        }
      } else {
        this.showError(this.yearSelect, 'Error loading years');
      }
    } catch (error) {
      console.error('Error loading years:', error);
      this.showError(this.yearSelect, 'Error loading years');
    } finally {
      this.showLoading(false);
    }
  }

  async fetchFilters(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.apiUrl}/api/vehicle-filters${queryString ? '?' + queryString : ''}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    return await response.json();
  }

  populateSelect(select, options, placeholder) {
    select.innerHTML = `<option value="">${placeholder}</option>`;
    options.forEach(option => {
      const optionEl = document.createElement('option');
      optionEl.value = option;
      optionEl.textContent = option;
      select.appendChild(optionEl);
    });
  }

  resetModelSelect() {
    if (!this.modelSelect) return;
    this.modelSelect.innerHTML = '<option value="">Select Model</option>';
    this.modelSelect.disabled = true;
  }

  resetYearSelect() {
    if (!this.yearSelect) return;
    this.yearSelect.innerHTML = '<option value="">Select Year</option>';
    this.yearSelect.disabled = true;
  }

  showError(select, message) {
    if (!select) return;
    select.innerHTML = `<option value="">${message}</option>`;
    select.disabled = true;
  }

  showLoading(show) {
    // Loading overlay disabled; rely on disabled selects styling instead
  }

  async handleSubmit() {
    if (!this.selectedMake) {
      alert('Please select a vehicle make');
      return;
    }

    // Redirect to all products collection with filters applied
    this.redirectToCollectionWithFilters();
  }
  
  redirectToCollectionWithFilters() {
    // Build filter URL parameters
    const filterParams = new URLSearchParams();
    
    if (this.selectedMake) {
      filterParams.append('filter.p.m.magento.make_filter', this.selectedMake);
    }
    if (this.selectedModel) {
      filterParams.append('filter.p.m.magento.model_filter', this.selectedModel);
    }
    if (this.selectedYear) {
      filterParams.append('filter.p.m.magento.year_filter', this.selectedYear);
    }
    
    // If we're on a collection page, stay on that collection and apply filters
    // Otherwise, redirect to /collections/all
    if (this.isCollectionPage && this.collectionHandle) {
      const currentUrl = new URL(window.location.href);
      const basePath = `/collections/${this.collectionHandle}`;
      const fullUrl = filterParams.toString() 
        ? `${basePath}?${filterParams.toString()}`
        : basePath;
      
      window.location.href = fullUrl;
    } else {
      // Not on a collection page, redirect to all products collection with filters
      const collectionUrl = '/collections/all';
      const fullUrl = filterParams.toString() 
        ? `${collectionUrl}?${filterParams.toString()}`
        : collectionUrl;
      
      window.location.href = fullUrl;
    }
  }

  async fetchProducts(params) {
    const queryString = new URLSearchParams(params).toString();
    const url = `${this.apiUrl}/api/products?${queryString}`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }
    
    return await response.json();
  }

  updateProductGrid(data) {
    const productGrid = document.querySelector('ul.product-grid');
    if (!productGrid) {
      console.error('Product grid not found');
      return;
    }

    if (!data.success || !data.products || data.products.length === 0) {
      this.showEmptyState(productGrid);
      return;
    }

    productGrid.innerHTML = data.products.map((product, index) => {
      return this.createProductCard(product, index);
    }).join('');

    const pagination = document.querySelector('.product-grid__pagination, [class*="pagination"]');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  createProductCard(product, index) {
    const price = parseFloat(product.price.amount) / 100;
    const formattedPrice = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: product.price.currency
    }).format(price);

    const imageUrl = product.image?.url || '';
    const imageAlt = product.image?.alt || product.title;
    const sku = product.sku || '';

    return `
      <li class="product-grid__item product-grid__item--${index}" data-product-id="${product.id}">
        <product-card-link class="product-card" href="${product.url}">
          <div class="
            product-card__content
            layout-panel-flex
            layout-panel-flex--column
            product-grid__card
            spacing-style
            border-style
            gap-style
          " style="
            --border-width: 1px; --border-style: none; --border-color: rgb(var(--color-border-rgb) / 1.0); --border-radius: 0px;
            --flex-direction: column; --flex-wrap: nowrap; --flex-wrap-mobile: wrap;
            --horizontal-alignment: ; --vertical-alignment: ; --vertical-alignment-mobile: ;
            --padding-block-start: 0px;--padding-block-end: 0px;--padding-inline-start: 0px;--padding-inline-end: 0px;
            --product-card-gap: 4px;
            --quick-add-display: none;
            --quick-add-mobile-display: none;
          ">
            
            <div class="card-gallery spacing-style border-style" style="
              --border-width: 1px; --border-style: solid; --border-color: rgb(var(--color-border-rgb) / 0.13); --border-radius: 0px;
              --padding-block-start: 0px;--padding-block-end: 0px;--padding-inline-start: 0px;--padding-inline-end: 0px;
              --gallery-aspect-ratio: 1;
            " data-product-id="${product.id}" data-image-ratio="square">
              <a class="contents" href="${product.url}" aria-label="${product.title}">
                <div class="product-media-container media-fit product-media-container--image" style="--product-media-fit: cover;">
                  <div class="product-media" style="--ratio: 1" data-media-id="${product.id}">
                    <img 
                      src="${imageUrl}" 
                      alt="${imageAlt}"
                      srcset="${imageUrl}?width=240 240w, ${imageUrl}?width=352 352w, ${imageUrl}?width=832 832w, ${imageUrl}?width=1200 1200w"
                      width="832"
                      height="832"
                      loading="lazy"
                      sizes="(min-width: 1780px) 20vw, (min-width: 1424px) 25vw, (min-width: 1068px) 33vw, (min-width: 750px) 50vw, 100vw"
                      class="product-media__image"
                      style="--focal-point: 50.0% 50.0%;"
                    />
                  </div>
                </div>
              </a>
              <div class="product-badges product-badges--top-right" style="
                --badge-border-radius: 100px;
                --badge-font-family: var(--font-body--family); --badge-font-weight: var(--font-body--weight); --badge-text-transform: none;
              "></div>
            </div>

            <div class="product-grid-view-zoom-out--details">
              <h3 class="h4">${product.title}</h3>
            </div>

            <a class="contents user-select-text" href="${product.url}">
              <div class="spacing-style text-block h3 text-block--align-left" style="
                --padding-block-start: 4px; --padding-block-end:0px;
                --padding-inline-start:0px; --padding-inline-end:0px;
                --color: var(--color-foreground);
                --width: 100%;
                --max-width: var(--max-width--body-normal);
                --text-align: left;
              ">
                <p>${product.title}</p>
              </div>
            </a>

            ${sku ? `
              <p class="product-card__sku">
                <strong>SKU#:</strong> ${sku}
              </p>
            ` : ''}

            <product-price class="text-block text-block--align-left text-left h6 spacing-style" data-product-id="${product.id}" style="
              --color: var(--color-foreground);
              --padding-block-start: 13px;--padding-block-end: 0px;--padding-inline-start: 0px;--padding-inline-end: 0px;
              --width: 100%;
            ">
              <div>
                <span class="price">${formattedPrice}</span>
              </div>
            </product-price>

            <a class="button product-card__view-details" href="${product.url}">
              View details
            </a>
          </div>
        </product-card-link>
      </li>
    `;
  }

  showEmptyState(productGrid) {
    if (productGrid) {
      productGrid.innerHTML = `
        <li class="product-grid__empty" style="grid-column: 1 / -1; text-align: center; padding: 48px 24px;">
          <h3 style="margin: 0 0 12px 0; font-size: 20px; color: #333;">No Products Found</h3>
          <p style="margin: 0; color: #666;">No products match your selected filters. Try different options.</p>
        </li>
      `;
    }

    const pagination = document.querySelector('.product-grid__pagination, [class*="pagination"]');
    if (pagination) {
      pagination.style.display = 'none';
    }

    this.updateProductCount(0);
  }

  updateProductCount(count) {
    const countElement = document.querySelector('.products-count-wrapper span');
    if (countElement) {
      const itemText = count === 1 ? 'item' : 'items';
      countElement.textContent = `${count} ${itemText}`;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new VehicleFilterAPI();
});
